// src/pages/TrackCreation/ModifyItemToProject.tsx

import React, { useState, useEffect } from "react";
import { uploadFileToS3Complete } from "../../api/s3";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAdminInfo, getItemFromProject, updateItemFromProject, extractFromAudio, getProjects } from "../../api";
import AudioPlayerRewind from "../../components/common/AudioPlayer";
import Dropdown from "../../components/common/Dropdown";
import { FileInput } from "../../components/common/FileInput";
import TextInput from "../../components/ui/TextInput";
import { useGlobalState } from "../../context/useGlobalState";
import { RefreshProjectProps } from "../../types/screensTypes";
import Icon from "../../components/common/Icon";
import { AUDIO_FILE_ACCEPT, isAcceptedAudioFile } from "../../utils/fileValidation";
import e from "./Edition.module.css";

interface User {
    company?: string;
    [key: string]: any; // Ajout pour gérer des propriétés dynamiques
}

interface FieldErrors {
    selectedContentLanguage: boolean;
    singleTitle: boolean;
}

const ModifyItemToProject: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const [, setUser] = useState<User>({});

    // ---------------------------
    // Shared state
    // ---------------------------
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [singleTitle, setSingleTitle] = useState<string>("");
    const [singleText, setSingleText] = useState<string>("");
    const [mp3File, setMp3File] = useState<string | null>(null); // single audio file
    // Voice is read-only at edition (info only — re-voicing would require regeneration).
    const [voiceName, setVoiceName] = useState<string>("");
    // Free-text voice name. Only offered when no voice row of ours produced this audio:
    // a real voice already carries its own identity, and letting it be renamed here would
    // create a second, contradictory mapping key at translation time.
    const [voiceLabel, setVoiceLabel] = useState<string>("");
    const [hasRealVoice, setHasRealVoice] = useState<boolean>(false);
    // Project (re)assignment: list of company projects + the item's current project.
    const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        selectedContentLanguage: false,
        singleTitle: false,
    });

    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const { id, itemId } = useParams<{ id: string; itemId: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<"text" | "audio">("text");
    const [audioTypeError, setAudioTypeError] = useState<boolean>(false);

    const wordCount = singleText.trim() ? singleText.trim().split(/\s+/).length : 0;
    const charCount = singleText.length;

    const downloadAudio = async () => {
        if (!mp3File) return;
        try {
            const response = await fetch(mp3File, { cache: "reload" });
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${singleTitle || "audio"}.mp3`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading audio:", error);
        }
    };

    // ---------------------------
    // Fetch user info
    // ---------------------------
    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setUser({});
                return;
            }

            const response = await getAdminInfo({ token });
            if (!response) {
                console.error("Error fetching user info:");
            } else {
                setUser({
                    ...response,
                    company: response.cmpany?.name || "",
                });
            }
        };

        fetchUser();
    }, []);

    // ---------------------------
    // Fetch item info
    // ---------------------------
    useEffect(() => {
        if (itemId && data.languagesAI && data.languagesAI.length > 0) {
            fetchItemInfo();
        }
        // fetch when item or languages become available
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, data.languagesAI]);

    const fetchItemInfo = async () => {
        const token = localStorage.getItem("token");
        if (!token || !id || !itemId) return;

        const response = await getItemFromProject({
            token,
            projectId: id,
            audioAIId: itemId,
        });
        if (response) {
            setSingleTitle(response.name || "");
            setSingleText(response.text || "");
            const findLanguage = data.languagesAI.find((lang) => lang.code === response.language);
            setSelectedContentLanguage(findLanguage?.value || "");
            setMp3File(response?.audioUrl || null);
            const rawItem = response as any;
            setVoiceName(rawItem?.voiceAI?.name || "");
            setVoiceLabel(rawItem?.voiceLabel || "");
            setHasRealVoice(Boolean(rawItem?.voiceAIId || rawItem?.voiceCloneId || rawItem?.voicePromptId));
            setSelectedProjectId(rawItem?.projectAIId || id || null);
        } else {
            console.error("Failed to fetch item");
        }
    };

    // Load the company's audio projects for the "Project" dropdown (assign / move / detach).
    useEffect(() => {
        const fetchProjectOptions = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            try {
                const projects = await getProjects({ token, itemType: "audio" });
                setProjectOptions(
                    (projects || []).map((p: { id: string; name: string }) => ({ value: p.id, label: p.name })),
                );
            } catch (err) {
                console.error("Failed to fetch projects:", err);
            }
        };
        fetchProjectOptions();
    }, []);

    // ---------------------------
    // File handler
    // ---------------------------
    const handleSingleFileSelected = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier

        if (!file) return;
        if (!isAcceptedAudioFile(file)) {
            setAudioTypeError(true);
            return;
        }
        setAudioTypeError(false);

        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No authentication token found");
            return;
        }

        uploadFileToS3Complete(
            {
                file,
                isPublic: true, // Public files for general uploads
                isFolder: false, // No folder structure for general uploads
            },
            token,
        )
            .then((result: string) => {
                setMp3File(result);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    // ---------------------------
    // Validation
    // ---------------------------
    useEffect(() => {
        const errors: FieldErrors = {
            selectedContentLanguage: !selectedContentLanguage,
            singleTitle: !singleTitle.trim(),
        };

        setFieldErrors(errors);
        setIsGenerateButtonDisabled(Object.values(errors).some((err) => err));
    }, [selectedContentLanguage, singleTitle, singleText, mp3File]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => {
            setShowErrors(false);
        }, 3000);
    };

    // ---------------------------
    // Submit / Save
    // ---------------------------
    const saveItem = async () => {
        setIsLoading(true);
        if (!singleTitle.trim() && !singleText.trim() && !selectedContentLanguage && !mp3File) {
            setIsLoading(false);
            showErrorMessage();
            return;
        }
        try {
            const token = localStorage.getItem("token");
            if (!token || !id || !itemId) return;
            const languageCode = data.languagesAI.find((lang) => lang.value === selectedContentLanguage)?.code;
            if (!languageCode) return;
            // Only send a project change when moving to a different, real project.
            // Detaching (no project) is intentionally not allowed here: items must stay
            // attached to a project, otherwise a manually-added audio (no generation queue)
            // would become unreachable in drafts.
            const targetProjectAIId = selectedProjectId && selectedProjectId !== id ? selectedProjectId : undefined;
            const resp = await updateItemFromProject({
                token,
                projectId: id,
                audioAIId: itemId,
                language: languageCode,
                title: singleTitle,
                text: singleText,
                audioUrl: mp3File,
                targetProjectAIId,
                // Sent even when empty: clearing the field is how a wrong name is removed.
                voiceLabel: hasRealVoice ? undefined : voiceLabel.trim(),
            });
            if (resp && resp.message) {
                // Follow the item to its new project when moved, otherwise stay on the current one.
                if (typeof targetProjectAIId === "string" && targetProjectAIId)
                    navigate(`/projects/${targetProjectAIId}`);
                else navigate(`/projects/${id}`);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to save item:", error);
            setIsLoading(false);
        }
    };

    const audioAIExtract = async () => {
        setIsLoading(true);
        if (!mp3File) {
            setIsLoading(false);
            return;
        }

        const token = localStorage.getItem("token");
        if (!token || !itemId) return;

        const resp = await extractFromAudio({
            token,
            audioFile: mp3File,
            audioAIId: itemId,
        });
        if (resp && resp.transcription) {
            setSingleText(resp.transcription);
        }
        setIsLoading(false);
    };

    const langCode = data.languagesAI.find((lang) => lang.value === selectedContentLanguage)?.code || "";

    return (
        <div className={e.page}>
            <button type="button" className={e.breadcrumb} onClick={() => navigate(`/projects/${id}`)}>
                <Icon name="ic-arrow-right" className={e.breadcrumbIcon} />
                {projectOptions.find((o) => o.value === id)?.label || t("allProjects")}
            </button>

            <div className={e.header}>
                <div className={e.titleRow}>
                    <h1 className={e.title}>{singleTitle || t("whatIsTheTitleOfYourStory")}</h1>
                    {langCode && <span className={e.badge}>{langCode.toUpperCase()}</span>}
                </div>
                <div className={e.headerActions}>
                    {mp3File && (
                        <button type="button" className={e.ghostBtn} onClick={downloadAudio}>
                            <Icon name="ic-download" className={e.ghostBtnIcon} />
                            {t("download")}
                        </button>
                    )}
                    <button
                        type="button"
                        className={e.primaryBtn}
                        onClick={saveItem}
                        disabled={isGenerateButtonDisabled || isLoading}
                    >
                        {isLoading ? t("processing") : t("save")}
                    </button>
                </div>
            </div>

            {/* Language / Voice (read-only) / Project (re)assignment */}
            <div className={e.metaCard}>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("language")}</p>
                    <Dropdown
                        label=""
                        options={data.languagesAI}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isMissing={showErrors && fieldErrors.selectedContentLanguage}
                        fullWidth
                        isWithoutMargin
                    />
                </div>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("voice")}</p>
                    {hasRealVoice ? (
                        <div className={e.readonlyValue}>{voiceName || "—"}</div>
                    ) : (
                        <>
                            <TextInput
                                placeholder={t("transcriptVoiceCustomPlaceholder")}
                                value={voiceLabel}
                                onChange={(ev) => setVoiceLabel(ev.target.value)}
                                fullWidth
                                size="small"
                            />
                            <p className={e.fieldHint}>{t("uploadVoiceNameSubtitle")}</p>
                        </>
                    )}
                </div>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("project")}</p>
                    <Dropdown
                        label=""
                        options={projectOptions}
                        onChange={(val) => {
                            // Ignore the empty default option: an item must always stay in a project.
                            if (val) setSelectedProjectId(val);
                        }}
                        loading={false}
                        error={false}
                        placeholder={t("selectAProject")}
                        value={selectedProjectId}
                        fullWidth
                        isWithoutMargin
                    />
                </div>
            </div>

            {/* Tabs */}
            <div className={e.tabs}>
                <button
                    className={`${e.tab} ${activeTab === "text" ? e.tabActive : ""}`}
                    onClick={() => setActiveTab("text")}
                >
                    {t("tabText")}
                </button>
                <button
                    className={`${e.tab} ${activeTab === "audio" ? e.tabActive : ""}`}
                    onClick={() => setActiveTab("audio")}
                >
                    {t("tabAudio")}
                </button>
            </div>

            {activeTab === "text" ? (
                <div className={e.panel}>
                    <div className={e.titleField}>
                        <p className={e.fieldLabel}>{t("whatIsTheTitleOfYourStory")}</p>
                        <TextInput
                            placeholder={t("pleaseInsertYourText")}
                            value={singleTitle}
                            onChange={(ev) => setSingleTitle(ev.target.value)}
                            size={"medium"}
                            isMissing={showErrors}
                        />
                    </div>
                    <div className={e.panelHeader}>
                        <div>
                            <h3 className={e.panelTitle}>{t("textEdition")}</h3>
                            <p className={e.panelSubtitle}>{t("textEditionSub")}</p>
                        </div>
                        {mp3File && (
                            <button className={e.extractBtn} disabled={isLoading} onClick={audioAIExtract}>
                                {isLoading ? t("processing") : t("extractFromAudio")}
                            </button>
                        )}
                    </div>
                    <TextInput
                        customClassName={e.textarea}
                        placeholder={t("pleaseInsertYourText")}
                        value={singleText}
                        onChange={(ev) => setSingleText(ev.target.value)}
                        size="big"
                        numberOfLines={12}
                    />
                    <div className={e.panelFooter}>
                        <span>
                            {wordCount} {t("wordsLabel")} · {charCount} {t("charactersLabel")}
                        </span>
                    </div>
                </div>
            ) : (
                <div className={e.panel}>
                    <div className={e.panelHeader}>
                        <div>
                            <h3 className={e.panelTitle}>{t("audioEdition")}</h3>
                            <p className={e.panelSubtitle}>{t("audioFormatHelptext")}</p>
                        </div>
                    </div>
                    <FileInput
                        onFileSelected={handleSingleFileSelected}
                        placeholder={t("dragAndDropOrClick")}
                        isMissing={showErrors}
                        acceptedFileTypes={AUDIO_FILE_ACCEPT}
                    />
                    {audioTypeError && <p className={e.errorHint}>{t("unsupportedFileType")}</p>}
                    {mp3File && (
                        <div style={{ marginTop: 18 }}>
                            <AudioPlayerRewind
                                title={singleTitle}
                                src={mp3File}
                                isAutoPlay={false}
                                onClickAddToProject={() => {}}
                                showAddtoProject={false}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ModifyItemToProject;
